import { useState, useEffect, useMemo } from 'react';
import ProcurementNav from '../components/ProcurementNav';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recipesApi, inventoryApi, menuApi } from '../services/api';
import api from '../services/api';
import {
  Plus,
  Search,
  ChefHat,
  DollarSign,
  Clock,
  Package,
  Link2,
  X,
  Edit2,
  Trash2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  PieChart,
  Eye,
  Calculator,
  Target,
  Lightbulb,
  CheckCircle2,
} from 'lucide-react';
import clsx from 'clsx';

interface RecipeIngredient {
  id: string;
  inventoryItemId: string;
  inventoryItemName?: string;
  quantity: number;
  unit: string;
  wasteFactor: number;
  isOptional: boolean;
  notes?: string;
  costCents?: number;
}

interface Recipe {
  id: string;
  name: string;
  description?: string;
  yield: number;
  yieldUnit: string;
  prepTimeMinutes: number;
  cookTimeMinutes: number;
  instructions?: string;
  menuItemId?: string;
  menuItemName?: string;
  totalCostCents?: number;
  costPerPortionCents?: number;
  ingredients: RecipeIngredient[];
  isActive: boolean;
}

interface InventoryItem {
  id: string;
  name: string;
  unit: string;
  currentQuantity: number;
  unitCostCents: number;
}

interface MenuItem {
  id: string;
  name: string;
  priceCents: number;
  categoryName?: string;
}

interface MenuCategory {
  id: string;
  name: string;
}

interface RecipeCostData {
  ingredients: Array<{
    inventoryItemName: string;
    lineCostCents: number;
    percentOfTotal: number;
  }>;
  totalCostCents: number;
  costPerPortionCents: number;
  menuItem?: {
    priceCents: number;
    foodCostPercent: number;
    profitMarginCents: number;
  };
}

const defaultRecipeForm = {
  name: '',
  description: '',
  yield: 1,
  yieldUnit: 'portion',
  prepTimeMinutes: 0,
  cookTimeMinutes: 0,
  laborMinutes: 0,
  instructions: '',
  menuItemId: '',
};

const defaultIngredientForm = {
  inventoryItemId: '',
  quantity: 0,
  unit: '',
  wasteFactor: 1.0,
  isOptional: false,
  notes: '',
};

export default function RecipesPage() {
  const { data: importedLib } = useQuery({
    queryKey: ['imported-recipes'],
    queryFn: async () => (await api.get('/v1/integrations/toast/imported-recipes')).data?.data,
    staleTime: 300_000,
  });
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [showIngredientModal, setShowIngredientModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showCostModal, setShowCostModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [selectedCostRecipeId, setSelectedCostRecipeId] = useState<string | null>(null);
  const [editingIngredient, setEditingIngredient] = useState<RecipeIngredient | null>(null);
  const [customPrice, setCustomPrice] = useState<number | null>(null);
  const [newMenuItemName, setNewMenuItemName] = useState('');
  const [selectedCategoryForNew, setSelectedCategoryForNew] = useState('');
  const [linkMenuItemId, setLinkMenuItemId] = useState('');
  const [recipeForm, setRecipeForm] = useState(defaultRecipeForm);
  const [ingredientForm, setIngredientForm] = useState(defaultIngredientForm);
  const [formError, setFormError] = useState('');

  // Fetch recipes
  const { data: recipesData, isLoading: loadingRecipes } = useQuery({
    queryKey: ['recipes'],
    queryFn: async () => {
      const response = await recipesApi.getAll();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });

  // Fetch inventory items for ingredient selection
  const { data: inventoryData } = useQuery({
    queryKey: ['inventory'],
    queryFn: async () => {
      const response = await inventoryApi.getItems();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });

  // Fetch menu items for linking
  const { data: menuItemsData } = useQuery({
    queryKey: ['menuItems'],
    queryFn: async () => {
      const response = await menuApi.getItems();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });

  // Fetch menu categories for creating new menu items
  const { data: categoriesData } = useQuery({
    queryKey: ['menuCategories'],
    queryFn: async () => {
      const response = await menuApi.getCategories();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });

  // Fetch cost data for selected recipe
  const { data: costData, isLoading: loadingCost } = useQuery({
    queryKey: ['recipeCost', selectedCostRecipeId],
    queryFn: async () => {
      if (!selectedCostRecipeId) return null;
      const response = await recipesApi.getCost(selectedCostRecipeId);
      return response.data?.data || response.data;
    },
    enabled: !!selectedCostRecipeId,
  });

  const recipes: Recipe[] = recipesData || [];
  const inventoryItems: InventoryItem[] = inventoryData || [];
  const menuItems: MenuItem[] = menuItemsData || [];
  const categories: MenuCategory[] = categoriesData || [];

  // Create recipe mutation
  const createRecipeMutation = useMutation({
    mutationFn: (data: typeof recipeForm) => recipesApi.create({
      name: data.name,
      description: data.description || undefined,
      yield: data.yield,
      yieldUnit: data.yieldUnit,
      prepTimeMinutes: data.prepTimeMinutes,
      cookTimeMinutes: data.cookTimeMinutes,
      laborMinutes: data.laborMinutes,
      instructions: data.instructions || undefined,
      menuItemId: data.menuItemId || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      setShowRecipeModal(false);
      setRecipeForm(defaultRecipeForm);
      setFormError('');
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to create recipe');
    },
  });

  // Update recipe mutation
  const updateRecipeMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<typeof recipeForm> }) =>
      recipesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      setShowRecipeModal(false);
      setSelectedRecipe(null);
      setRecipeForm(defaultRecipeForm);
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to update recipe');
    },
  });

  // Delete recipe mutation
  const deleteRecipeMutation = useMutation({
    mutationFn: (id: string) => recipesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      setSelectedRecipe(null);
    },
  });

  // Add ingredient mutation
  const addIngredientMutation = useMutation({
    mutationFn: ({ recipeId, data }: { recipeId: string; data: typeof ingredientForm }) =>
      recipesApi.addIngredient(recipeId, {
        inventoryItemId: data.inventoryItemId,
        quantity: data.quantity,
        unit: data.unit,
        wasteFactor: data.wasteFactor,
        isOptional: data.isOptional,
        notes: data.notes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      setShowIngredientModal(false);
      setIngredientForm(defaultIngredientForm);
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to add ingredient');
    },
  });

  // Update ingredient mutation
  const updateIngredientMutation = useMutation({
    mutationFn: ({ recipeId, ingredientId, data }: { recipeId: string; ingredientId: string; data: Partial<typeof ingredientForm> }) =>
      recipesApi.updateIngredient(recipeId, ingredientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      setShowIngredientModal(false);
      setEditingIngredient(null);
      setIngredientForm(defaultIngredientForm);
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to update ingredient');
    },
  });

  // Remove ingredient mutation
  const removeIngredientMutation = useMutation({
    mutationFn: ({ recipeId, ingredientId }: { recipeId: string; ingredientId: string }) =>
      recipesApi.removeIngredient(recipeId, ingredientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
    },
  });

  // Link menu item mutation
  const linkMenuItemMutation = useMutation({
    mutationFn: ({ recipeId, menuItemId }: { recipeId: string; menuItemId: string }) =>
      recipesApi.linkMenuItem(recipeId, menuItemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['menuItems'] });
      setShowLinkModal(false);
      setSelectedRecipe(null);
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to link menu item');
    },
  });

  // Update menu item price mutation
  const updateMenuPriceMutation = useMutation({
    mutationFn: ({ menuItemId, priceCents }: { menuItemId: string; priceCents: number }) =>
      menuApi.updateItem(menuItemId, { priceCents }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['menuItems'] });
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to update price');
    },
  });

  // Create menu item from recipe mutation
  const createMenuItemMutation = useMutation({
    mutationFn: async ({ name, priceCents, categoryId, recipeId }: {
      name: string;
      priceCents: number;
      categoryId?: string;
      recipeId: string;
    }) => {
      // First create the menu item
      const response = await menuApi.createItem({ name, priceCents, categoryId, isAvailable: true });
      const newMenuItem = response.data?.data || response.data;
      // Then link it to the recipe
      await recipesApi.linkMenuItem(recipeId, newMenuItem.id);
      return newMenuItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['menuItems'] });
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to create menu item');
    },
  });

  const filteredRecipes = recipes.filter((recipe) =>
    recipe.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const linkedRecipes = recipes.filter(r => r.menuItemId);
    const menuItemMap = new Map(menuItems.map(m => [m.id, m]));

    let totalFoodCostPercent = 0;
    let highMarginCount = 0; // >70%
    let mediumMarginCount = 0; // 50-70%
    let lowMarginCount = 0; // <50%
    let totalRecipeValue = 0;
    let validCount = 0;

    linkedRecipes.forEach(recipe => {
      const menuItem = recipe.menuItemId ? menuItemMap.get(recipe.menuItemId) : null;
      if (menuItem && recipe.costPerPortionCents && menuItem.priceCents > 0) {
        const foodCostPercent = (recipe.costPerPortionCents / menuItem.priceCents) * 100;
        const marginPercent = 100 - foodCostPercent;

        totalFoodCostPercent += foodCostPercent;
        totalRecipeValue += recipe.costPerPortionCents;
        validCount++;

        if (marginPercent >= 70) highMarginCount++;
        else if (marginPercent >= 50) mediumMarginCount++;
        else lowMarginCount++;
      }
    });

    return {
      avgFoodCostPercent: validCount > 0 ? totalFoodCostPercent / validCount : 0,
      highMarginCount,
      mediumMarginCount,
      lowMarginCount,
      totalRecipeValue,
      linkedCount: linkedRecipes.length,
      totalCount: recipes.length,
    };
  }, [recipes, menuItems]);

  // Get margin info for a recipe
  const getMarginInfo = (recipe: Recipe) => {
    if (!recipe.menuItemId || !recipe.costPerPortionCents) return null;
    const menuItem = menuItems.find(m => m.id === recipe.menuItemId);
    if (!menuItem || menuItem.priceCents <= 0) return null;

    const foodCostPercent = (recipe.costPerPortionCents / menuItem.priceCents) * 100;
    const marginPercent = 100 - foodCostPercent;
    const profitCents = menuItem.priceCents - recipe.costPerPortionCents;

    return {
      foodCostPercent,
      marginPercent,
      profitCents,
      priceCents: menuItem.priceCents,
    };
  };

  // Get margin color class
  const getMarginColor = (marginPercent: number) => {
    if (marginPercent >= 70) return 'text-green-600 bg-green-50';
    if (marginPercent >= 50) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const formatCurrency = (cents?: number) =>
    cents ? `$${(cents / 100).toFixed(2)}` : '$0.00';

  const formatTime = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const openEditRecipeModal = (recipe: Recipe) => {
    setSelectedRecipe(recipe);
    setRecipeForm({
      name: recipe.name,
      description: recipe.description || '',
      yield: recipe.yield,
      yieldUnit: recipe.yieldUnit,
      prepTimeMinutes: recipe.prepTimeMinutes,
      cookTimeMinutes: recipe.cookTimeMinutes,
      laborMinutes: (recipe as any).laborMinutes || 0,
      instructions: recipe.instructions || '',
      menuItemId: recipe.menuItemId || '',
    });
    setShowRecipeModal(true);
  };

  const openIngredientModal = (recipe: Recipe, ingredient?: RecipeIngredient) => {
    setSelectedRecipe(recipe);
    if (ingredient) {
      setEditingIngredient(ingredient);
      setIngredientForm({
        inventoryItemId: ingredient.inventoryItemId,
        quantity: ingredient.quantity,
        unit: ingredient.unit,
        wasteFactor: ingredient.wasteFactor,
        isOptional: ingredient.isOptional,
        notes: ingredient.notes || '',
      });
    } else {
      setEditingIngredient(null);
      setIngredientForm(defaultIngredientForm);
    }
    setShowIngredientModal(true);
  };

  const openPricingModal = (recipe: Recipe) => {
    setSelectedRecipe(recipe);
    setSelectedCostRecipeId(recipe.id); // Also fetch cost data for ingredient breakdown
    // Initialize custom price with current menu price if linked
    const menuItem = recipe.menuItemId ? menuItems.find(m => m.id === recipe.menuItemId) : null;
    setCustomPrice(menuItem?.priceCents || null);
    setNewMenuItemName(recipe.name);
    setSelectedCategoryForNew('');
    setLinkMenuItemId('');
    setFormError('');
    setShowPricingModal(true);
  };

  // Apply price to menu item
  const applyPrice = (priceCents: number) => {
    if (!selectedRecipe?.menuItemId) return;
    updateMenuPriceMutation.mutate({
      menuItemId: selectedRecipe.menuItemId,
      priceCents,
    });
    setCustomPrice(priceCents);
  };

  // Link existing menu item from pricing modal
  const linkFromPricing = () => {
    if (!selectedRecipe || !linkMenuItemId) return;
    linkMenuItemMutation.mutate({
      recipeId: selectedRecipe.id,
      menuItemId: linkMenuItemId,
    });
  };

  // Create new menu item from pricing modal
  const createMenuItemFromPricing = (priceCents: number) => {
    if (!selectedRecipe || !newMenuItemName.trim()) return;
    createMenuItemMutation.mutate({
      name: newMenuItemName.trim(),
      priceCents,
      categoryId: selectedCategoryForNew || undefined,
      recipeId: selectedRecipe.id,
    });
  };

  // Calculate metrics for a given price
  const calculatePriceMetrics = (costCents: number, priceCents: number) => {
    if (priceCents <= 0) return null;
    const foodCostPercent = (costCents / priceCents) * 100;
    const marginPercent = 100 - foodCostPercent;
    const profitCents = priceCents - costCents;
    return { foodCostPercent, marginPercent, profitCents };
  };

  const openLinkModal = (recipe: Recipe) => {
    setSelectedRecipe(recipe);
    setShowLinkModal(true);
  };

  const openCostModal = (recipe: Recipe) => {
    setSelectedRecipe(recipe);
    setSelectedCostRecipeId(recipe.id);
    setShowCostModal(true);
  };

  const handleSaveRecipe = () => {
    if (!recipeForm.name.trim()) {
      setFormError('Recipe name is required');
      return;
    }

    if (selectedRecipe) {
      updateRecipeMutation.mutate({ id: selectedRecipe.id, data: recipeForm });
    } else {
      createRecipeMutation.mutate(recipeForm);
    }
  };

  const handleSaveIngredient = () => {
    if (!ingredientForm.inventoryItemId || ingredientForm.quantity <= 0) {
      setFormError('Please select an ingredient and enter a valid quantity');
      return;
    }

    if (selectedRecipe) {
      if (editingIngredient) {
        // Update existing ingredient
        updateIngredientMutation.mutate({
          recipeId: selectedRecipe.id,
          ingredientId: editingIngredient.id,
          data: {
            quantity: ingredientForm.quantity,
            unit: ingredientForm.unit,
            wasteFactor: ingredientForm.wasteFactor,
            isOptional: ingredientForm.isOptional,
            notes: ingredientForm.notes || undefined,
          },
        });
      } else {
        // Add new ingredient — a "recipe:<id>" selection is a sub-recipe component
        const isSub = ingredientForm.inventoryItemId.startsWith('recipe:');
        addIngredientMutation.mutate({
          recipeId: selectedRecipe.id,
          data: isSub
            ? ({ ...ingredientForm, inventoryItemId: undefined, subRecipeId: ingredientForm.inventoryItemId.slice(7) } as any)
            : ingredientForm,
        });
      }
    }
  };

  // Pricing suggestion calculations
  const getPricingSuggestions = (costCents: number) => {
    // Industry standard food cost percentages for different restaurant types
    const tiers = [
      { name: 'Premium', foodCostPercent: 25, description: 'High-end dining, specialty items' },
      { name: 'Standard', foodCostPercent: 30, description: 'Most full-service restaurants' },
      { name: 'Value', foodCostPercent: 35, description: 'Casual dining, higher volume' },
      { name: 'Budget', foodCostPercent: 40, description: 'Fast casual, maximum value' },
    ];

    return tiers.map(tier => ({
      ...tier,
      suggestedPrice: Math.ceil((costCents / tier.foodCostPercent) * 100),
      profitPerItem: Math.ceil((costCents / tier.foodCostPercent) * 100) - costCents,
      marginPercent: 100 - tier.foodCostPercent,
    }));
  };

  // Update unit when inventory item changes
  useEffect(() => {
    if (ingredientForm.inventoryItemId) {
      const item = inventoryItems.find((i) => i.id === ingredientForm.inventoryItemId);
      if (item) {
        setIngredientForm((prev) => ({ ...prev, unit: item.unit }));
      }
    }
  }, [ingredientForm.inventoryItemId, inventoryItems]);

  return (
    <div className="p-6">
      <ProcurementNav />
      {(importedLib?.recipes?.length || 0) > 0 && (
        <div className="card p-5 mb-6">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-semibold text-slate-900">Your recipe library — from {importedLib?.meta?.source || 'your import'}</h2>
            <span className="text-xs text-slate-400">{importedLib!.recipes.length} recipes · costs as exported</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
            {importedLib!.recipes.map((r: any) => (
              <div key={r.name} className="flex items-baseline justify-between text-sm">
                <span className="text-slate-700 truncate pr-2">{r.name.replace(/^(MI|PREP)\s+/, '')}</span>
                <span className="tabular-nums text-slate-900 font-medium">${(r.costCents / 100).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">These are your real recipe costs, imported — they already power the Margins page. Build any of them out below to track ingredients and price changes in Mise.</p>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recipes</h1>
          <p className="text-gray-600">
            Manage recipes and link them to menu items for cost tracking
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedRecipe(null);
            setRecipeForm(defaultRecipeForm);
            setShowRecipeModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
        >
          <Plus size={20} />
          Add Recipe
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search recipes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
        </div>
      </div>

      {/* Summary Stats Bar */}
      {recipes.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <PieChart size={16} />
              Avg Food Cost
            </div>
            <div className="text-2xl font-semibold text-gray-900">
              {summaryStats.avgFoodCostPercent.toFixed(1)}%
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {summaryStats.linkedCount} of {summaryStats.totalCount} linked
            </div>
          </div>

          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-green-600 text-sm mb-1">
              <TrendingUp size={16} />
              High Margin
            </div>
            <div className="text-2xl font-semibold text-green-600">
              {summaryStats.highMarginCount}
            </div>
            <div className="text-xs text-gray-500 mt-1">70%+ margin</div>
          </div>

          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-yellow-600 text-sm mb-1">
              <TrendingDown size={16} />
              Medium Margin
            </div>
            <div className="text-2xl font-semibold text-yellow-600">
              {summaryStats.mediumMarginCount}
            </div>
            <div className="text-xs text-gray-500 mt-1">50-70% margin</div>
          </div>

          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-red-600 text-sm mb-1">
              <AlertCircle size={16} />
              Low Margin
            </div>
            <div className="text-2xl font-semibold text-red-600">
              {summaryStats.lowMarginCount}
            </div>
            <div className="text-xs text-gray-500 mt-1">&lt;50% margin</div>
          </div>
        </div>
      )}

      {/* Recipes Grid */}
      {loadingRecipes ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
        </div>
      ) : filteredRecipes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border">
          <ChefHat className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">No recipes</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchQuery ? 'No recipes match your search' : 'Get started by creating your first recipe'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => {
                setSelectedRecipe(null);
                setRecipeForm(defaultRecipeForm);
                setShowRecipeModal(true);
              }}
              className="mt-4 text-amber-600 hover:text-amber-700 font-medium"
            >
              Create Recipe
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRecipes.map((recipe) => (
            <div
              key={recipe.id}
              className="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{recipe.name}</h3>
                  {recipe.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {recipe.description}
                    </p>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openPricingModal(recipe)}
                    className="p-1 text-gray-400 hover:text-green-600"
                    title="Pricing Calculator"
                  >
                    <Calculator size={16} />
                  </button>
                  <button
                    onClick={() => openEditRecipeModal(recipe)}
                    className="p-1 text-gray-400 hover:text-amber-600"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Delete this recipe?')) {
                        deleteRecipeMutation.mutate(recipe.id);
                      }
                    }}
                    className="p-1 text-gray-400 hover:text-red-600"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-3 mb-3 text-sm">
                <div className="flex items-center gap-1 text-gray-600">
                  <DollarSign size={14} />
                  <span>{formatCurrency(recipe.costPerPortionCents)}/{recipe.yieldUnit === 'cases' ? 'case' : (recipe.yieldUnit || 'portion')}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-600">
                  <Clock size={14} />
                  <span>{formatTime(recipe.prepTimeMinutes + recipe.cookTimeMinutes)}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-600">
                  <Package size={14} />
                  <span>{recipe.ingredients?.length || 0} ingredients</span>
                </div>
              </div>

              {/* Margin & Food Cost Info */}
              {(() => {
                const marginInfo = getMarginInfo(recipe);
                if (marginInfo) {
                  return (
                    <div className="flex gap-2 mb-3">
                      <div className={clsx(
                        'flex items-center gap-1 px-2 py-1 rounded text-sm font-medium',
                        getMarginColor(marginInfo.marginPercent)
                      )}>
                        <TrendingUp size={14} />
                        <span>{marginInfo.marginPercent.toFixed(1)}% margin</span>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 rounded text-sm bg-gray-100 text-gray-600">
                        <span>{marginInfo.foodCostPercent.toFixed(1)}% food cost</span>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Linked Menu Item */}
              {recipe.menuItemName ? (
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-sm bg-green-50 text-green-700 px-2 py-1 rounded">
                    <Link2 size={14} />
                    <span>Linked to: {recipe.menuItemName}</span>
                  </div>
                  <button
                    onClick={() => openCostModal(recipe)}
                    className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700"
                    title="View cost breakdown"
                  >
                    <Eye size={14} />
                    Costs
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => openLinkModal(recipe)}
                  className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700 mb-3"
                >
                  <Link2 size={14} />
                  Link to menu item
                </button>
              )}

              {/* Ingredients Preview */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500 uppercase">Ingredients</span>
                  <div className="flex items-center gap-2">
                    {recipe.ingredients?.length > 0 && (
                      <button
                        onClick={() => openCostModal(recipe)}
                        className="text-xs text-gray-500 hover:text-amber-600"
                        title="View cost breakdown"
                      >
                        <Eye size={12} />
                      </button>
                    )}
                    <button
                      onClick={() => openIngredientModal(recipe)}
                      className="text-xs text-amber-600 hover:text-amber-700"
                    >
                      + Add
                    </button>
                  </div>
                </div>
                {recipe.ingredients?.length > 0 ? (
                  <ul className="space-y-2">
                    {recipe.ingredients.slice(0, 4).map((ing) => {
                      const costPercent = recipe.totalCostCents && ing.costCents
                        ? (ing.costCents / recipe.totalCostCents) * 100
                        : null;
                      return (
                        <li
                          key={ing.id}
                          className="text-sm group"
                        >
                          <div className="flex items-center justify-between">
                            <span className={clsx(
                              'font-medium text-gray-900',
                              ing.isOptional && 'italic text-gray-500'
                            )}>
                              {ing.inventoryItemName || (ing as any).subRecipeName || 'Unknown'}
                            </span>
                            <div className="flex items-center gap-2">
                              {ing.costCents !== undefined && (
                                <span className="text-amber-600 font-semibold">
                                  {formatCurrency(ing.costCents)}
                                </span>
                              )}
                              <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
                                <button
                                  onClick={() => openIngredientModal(recipe, ing)}
                                  className="p-1 text-gray-400 hover:text-amber-600"
                                  title="Edit ingredient"
                                >
                                  <Edit2 size={12} />
                                </button>
                                <button
                                  onClick={() => removeIngredientMutation.mutate({ recipeId: recipe.id, ingredientId: ing.id })}
                                  className="p-1 text-gray-400 hover:text-red-600"
                                  title="Remove ingredient"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-500 mt-0.5">
                            <span>{ing.quantity} {ing.unit}{ing.wasteFactor > 1 ? ` (${Math.round((ing.wasteFactor - 1) * 100)}% waste)` : ''}</span>
                            {costPercent !== null && (
                              <span className="flex items-center gap-1">
                                <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-amber-400 rounded-full"
                                    style={{ width: `${Math.min(costPercent, 100)}%` }}
                                  />
                                </div>
                                <span>{costPercent.toFixed(0)}%</span>
                              </span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                    {recipe.ingredients.length > 4 && (
                      <li className="text-xs text-gray-400 pt-1">
                        +{recipe.ingredients.length - 4} more ingredients
                      </li>
                    )}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-400">No ingredients added</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recipe Modal */}
      {showRecipeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {selectedRecipe ? 'Edit Recipe' : 'New Recipe'}
              </h2>
              <button
                onClick={() => {
                  setShowRecipeModal(false);
                  setSelectedRecipe(null);
                  setFormError('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {formError && (
                <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded">
                  <AlertCircle size={16} />
                  <span className="text-sm">{formError}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipe Name *
                </label>
                <input
                  type="text"
                  value={recipeForm.name}
                  onChange={(e) => setRecipeForm({ ...recipeForm, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                  placeholder="e.g., Caesar Salad"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={recipeForm.description}
                  onChange={(e) => setRecipeForm({ ...recipeForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                  rows={2}
                  placeholder="Brief description..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Yield
                  </label>
                  <input
                    type="number"
                    min="0.25"
                    step="0.25"
                    value={recipeForm.yield}
                    onChange={(e) =>
                      setRecipeForm({ ...recipeForm, yield: parseFloat(e.target.value) || 1 })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Yield Unit
                  </label>
                  <select
                    value={recipeForm.yieldUnit}
                    onChange={(e) => setRecipeForm({ ...recipeForm, yieldUnit: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="portion">portion</option>
                    <option value="cases">cases</option>
                    <option value="serving">serving</option>
                    <option value="batch">batch</option>
                    <option value="each">each</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prep Time (min)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={recipeForm.prepTimeMinutes}
                    onChange={(e) =>
                      setRecipeForm({ ...recipeForm, prepTimeMinutes: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cook Time (min)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={recipeForm.cookTimeMinutes}
                    onChange={(e) =>
                      setRecipeForm({ ...recipeForm, cookTimeMinutes: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Labour (min)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={recipeForm.laborMinutes}
                    onChange={(e) =>
                      setRecipeForm({ ...recipeForm, laborMinutes: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    title="Hands-on time that should count toward this recipe's cost (× your labour rate in Settings)"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instructions
                </label>
                <textarea
                  value={recipeForm.instructions}
                  onChange={(e) => setRecipeForm({ ...recipeForm, instructions: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                  rows={4}
                  placeholder="Step by step instructions..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => {
                  setShowRecipeModal(false);
                  setSelectedRecipe(null);
                  setFormError('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRecipe}
                disabled={createRecipeMutation.isPending || updateRecipeMutation.isPending}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {createRecipeMutation.isPending || updateRecipeMutation.isPending
                  ? 'Saving...'
                  : selectedRecipe
                  ? 'Update Recipe'
                  : 'Create Recipe'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Ingredient Modal */}
      {showIngredientModal && selectedRecipe && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingIngredient ? 'Edit Ingredient' : 'Add Ingredient'}
              </h2>
              <button
                onClick={() => {
                  setShowIngredientModal(false);
                  setEditingIngredient(null);
                  setFormError('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {formError && (
                <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded">
                  <AlertCircle size={16} />
                  <span className="text-sm">{formError}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Inventory Item *
                </label>
                <select
                  value={ingredientForm.inventoryItemId}
                  onChange={(e) =>
                    setIngredientForm({ ...ingredientForm, inventoryItemId: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Select an item...</option>
                  <optgroup label="Ingredients">
                    {inventoryItems.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name} ({item.unit})
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Recipe components (sub-recipes)">
                    {recipes
                      .filter((r) => r.id !== selectedRecipe?.id)
                      .map((r) => (
                        <option key={r.id} value={`recipe:${r.id}`}>
                          {r.name} (recipe)
                        </option>
                      ))}
                  </optgroup>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    min="0.001"
                    step="0.001"
                    value={ingredientForm.quantity || ''}
                    onChange={(e) =>
                      setIngredientForm({
                        ...ingredientForm,
                        quantity: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit
                  </label>
                  <input
                    type="text"
                    value={ingredientForm.unit}
                    onChange={(e) =>
                      setIngredientForm({ ...ingredientForm, unit: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="oz, cups, etc."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Waste Factor
                </label>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={ingredientForm.wasteFactor}
                  onChange={(e) =>
                    setIngredientForm({
                      ...ingredientForm,
                      wasteFactor: parseFloat(e.target.value) || 1,
                    })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  1.0 = no waste, 1.1 = 10% waste, 1.25 = 25% waste
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isOptional"
                  checked={ingredientForm.isOptional}
                  onChange={(e) =>
                    setIngredientForm({ ...ingredientForm, isOptional: e.target.checked })
                  }
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <label htmlFor="isOptional" className="text-sm text-gray-700">
                  Optional ingredient (for modifiers, garnishes)
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <input
                  type="text"
                  value={ingredientForm.notes}
                  onChange={(e) =>
                    setIngredientForm({ ...ingredientForm, notes: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                  placeholder="e.g., diced, room temperature..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => {
                  setShowIngredientModal(false);
                  setEditingIngredient(null);
                  setFormError('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveIngredient}
                disabled={addIngredientMutation.isPending || updateIngredientMutation.isPending}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {addIngredientMutation.isPending || updateIngredientMutation.isPending
                  ? 'Saving...'
                  : editingIngredient
                  ? 'Update Ingredient'
                  : 'Add Ingredient'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link Menu Item Modal */}
      {showLinkModal && selectedRecipe && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Link to Menu Item</h2>
              <button
                onClick={() => setShowLinkModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Link <strong>{selectedRecipe.name}</strong> to a menu item to enable automatic
                inventory deduction and cost tracking.
              </p>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() =>
                      linkMenuItemMutation.mutate({
                        recipeId: selectedRecipe.id,
                        menuItemId: item.id,
                      })
                    }
                    disabled={linkMenuItemMutation.isPending}
                    className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-amber-50 hover:border-amber-300 text-left"
                  >
                    <span className="font-medium">{item.name}</span>
                    <span className="text-gray-500">{formatCurrency(item.priceCents)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end p-4 border-t">
              <button
                onClick={() => setShowLinkModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cost Breakdown Modal */}
      {showCostModal && selectedRecipe && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold">Cost Breakdown</h2>
                <p className="text-sm text-gray-500">{selectedRecipe.name}</p>
              </div>
              <button
                onClick={() => {
                  setShowCostModal(false);
                  setSelectedCostRecipeId(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4">
              {loadingCost ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
                </div>
              ) : costData ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 uppercase mb-1">Total Cost</div>
                      <div className="text-xl font-semibold text-gray-900">
                        {formatCurrency(costData.totalCostCents)}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 uppercase mb-1">Per Portion</div>
                      <div className="text-xl font-semibold text-gray-900">
                        {formatCurrency(costData.costPerPortionCents)}
                      </div>
                    </div>
                  </div>

                  {/* Ingredients vs labour — same numbers as the costing engine */}
                  {((costData as any).laborCostCents || 0) > 0 && (
                    <div className="mb-4 text-sm text-gray-600 flex items-center gap-4">
                      <span>Ingredients {formatCurrency((costData as any).ingredientCostCents || 0)}</span>
                      <span>·</span>
                      <span>Labour {formatCurrency((costData as any).laborCostCents || 0)}</span>
                    </div>
                  )}
                  {((costData as any).costWarnings || []).length > 0 && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 space-y-1">
                      {((costData as any).costWarnings as string[]).map((w, i) => (
                        <p key={i}>{w}</p>
                      ))}
                    </div>
                  )}

                  {/* Margin Info (if linked to menu item) */}
                  {costData.menuItem && costData.menuItem.foodCostPercent != null && (
                    <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Menu Price</span>
                        <span className="font-semibold">{formatCurrency(costData.menuItem.priceCents)}</span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Food Cost %</span>
                        <span className={clsx(
                          'font-semibold px-2 py-0.5 rounded',
                          costData.menuItem.foodCostPercent <= 30 ? 'bg-green-100 text-green-700' :
                          costData.menuItem.foodCostPercent <= 40 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        )}>
                          {costData.menuItem.foodCostPercent.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Profit Margin</span>
                        <span className={clsx(
                          'font-semibold px-2 py-0.5 rounded',
                          getMarginColor(100 - costData.menuItem.foodCostPercent)
                        )}>
                          {formatCurrency(costData.menuItem.profitMarginCents)} ({(100 - costData.menuItem.foodCostPercent).toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Ingredient Breakdown */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 border-b">
                      <span className="text-xs font-medium text-gray-500 uppercase">
                        Ingredient Costs
                      </span>
                    </div>
                    <ul className="divide-y">
                      {costData.ingredients?.map((ing: RecipeCostData['ingredients'][0], index: number) => (
                        <li key={index} className="flex items-center justify-between px-3 py-2">
                          <span className="text-sm text-gray-700">{ing.inventoryItemName || (ing as any).subRecipeName}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(ing.lineCostCents)}
                            </span>
                            <div className="w-16">
                              <div className="flex items-center gap-1">
                                <div
                                  className="h-2 bg-amber-500 rounded"
                                  style={{ width: `${Math.min(ing.percentOfTotal, 100)}%` }}
                                />
                                <span className="text-xs text-gray-500">
                                  {ing.percentOfTotal.toFixed(0)}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <AlertCircle className="mx-auto h-8 w-8 mb-2" />
                  <p>Unable to load cost data</p>
                </div>
              )}
            </div>

            <div className="flex justify-end p-4 border-t">
              <button
                onClick={() => {
                  setShowCostModal(false);
                  setSelectedCostRecipeId(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Assistant Modal */}
      {showPricingModal && selectedRecipe && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Calculator className="text-green-600" size={20} />
                  Pricing Assistant
                </h2>
                <p className="text-sm text-gray-500">{selectedRecipe.name}</p>
              </div>
              <button
                onClick={() => setShowPricingModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4">
              {/* Current Cost Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="text-amber-600" size={18} />
                  <span className="font-medium text-gray-900">Your Recipe Cost</span>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="text-sm text-gray-500">Cost per {selectedRecipe.yieldUnit === 'cases' ? 'Case' : 'Portion'}</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {formatCurrency(selectedRecipe.costPerPortionCents)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Total Recipe Cost</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {formatCurrency(selectedRecipe.totalCostCents)}
                    </div>
                    <div className="text-xs text-gray-500">
                      (yields {selectedRecipe.yield} {selectedRecipe.yieldUnit}{selectedRecipe.yield > 1 ? 's' : ''})
                    </div>
                  </div>
                </div>

                {/* Ingredient Cost Breakdown */}
                {costData && costData.ingredients?.length > 0 && (
                  <div className="border-t pt-3">
                    <div className="text-xs font-medium text-gray-500 uppercase mb-2">Ingredient Costs</div>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {costData.ingredients.map((ing: RecipeCostData['ingredients'][0], index: number) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700 truncate flex-1">{ing.inventoryItemName || (ing as any).subRecipeName || 'Sub-recipe'}</span>
                          <div className="flex items-center gap-2 ml-2">
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-amber-500 rounded-full"
                                style={{ width: `${Math.min(ing.percentOfTotal, 100)}%` }}
                              />
                            </div>
                            <span className="text-gray-500 text-xs w-8">{ing.percentOfTotal.toFixed(0)}%</span>
                            <span className="font-medium text-gray-900 w-14 text-right">
                              {formatCurrency(ing.lineCostCents)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {loadingCost && (
                  <div className="border-t pt-3 flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-600" />
                    <span className="ml-2 text-sm text-gray-500">Loading ingredients...</span>
                  </div>
                )}
              </div>

              {/* Menu Item Linking Section - shown when recipe is not linked */}
              {!selectedRecipe.menuItemId && (
                <div className="mb-6 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-300 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Link2 className="text-amber-600" size={18} />
                    <span className="font-medium text-gray-900">Link to Menu</span>
                    <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Required for pricing</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Connect this recipe to a menu item to track margins and apply prices directly.
                  </p>

                  {formError && (
                    <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded mb-4">
                      <AlertCircle size={16} />
                      <span className="text-sm">{formError}</span>
                    </div>
                  )}

                  {/* Tabs for Link vs Create */}
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => setLinkMenuItemId('')}
                      className={clsx(
                        'flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors',
                        !linkMenuItemId
                          ? 'bg-amber-600 text-white'
                          : 'bg-white text-gray-600 border border-gray-300 hover:border-amber-400'
                      )}
                    >
                      Create New Menu Item
                    </button>
                    <button
                      onClick={() => setLinkMenuItemId('select')}
                      className={clsx(
                        'flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors',
                        linkMenuItemId
                          ? 'bg-amber-600 text-white'
                          : 'bg-white text-gray-600 border border-gray-300 hover:border-amber-400'
                      )}
                    >
                      Link Existing Item
                    </button>
                  </div>

                  {!linkMenuItemId ? (
                    // Create new menu item form
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Menu Item Name *
                        </label>
                        <input
                          type="text"
                          value={newMenuItemName}
                          onChange={(e) => setNewMenuItemName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="e.g., Grilled Salmon"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category (optional)
                        </label>
                        <select
                          value={selectedCategoryForNew}
                          onChange={(e) => setSelectedCategoryForNew(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        >
                          <option value="">Select a category...</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <p className="text-xs text-gray-500">
                        Choose a price below or enter a custom price to create the menu item
                      </p>
                    </div>
                  ) : (
                    // Link existing menu item
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Menu Item
                        </label>
                        <select
                          value={linkMenuItemId === 'select' ? '' : linkMenuItemId}
                          onChange={(e) => setLinkMenuItemId(e.target.value || 'select')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        >
                          <option value="">Choose a menu item...</option>
                          {menuItems.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name} - {formatCurrency(item.priceCents)}
                            </option>
                          ))}
                        </select>
                      </div>
                      {linkMenuItemId && linkMenuItemId !== 'select' && (
                        <button
                          onClick={linkFromPricing}
                          disabled={linkMenuItemMutation.isPending}
                          className="w-full py-2 px-4 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 font-medium"
                        >
                          {linkMenuItemMutation.isPending ? 'Linking...' : 'Link to This Menu Item'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Current Menu Item Info - shown when recipe is linked */}
              {selectedRecipe.menuItemId && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="text-green-600" size={18} />
                      <span className="font-medium text-gray-900">Linked to Menu</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{selectedRecipe.menuItemName}</div>
                      <div className="text-sm text-gray-500">
                        Current price: {formatCurrency(menuItems.find(m => m.id === selectedRecipe.menuItemId)?.priceCents)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Interactive Price Calculator */}
              {selectedRecipe.costPerPortionCents && (
                <div className="mb-6 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Calculator className="text-green-600" size={18} />
                    <span className="font-medium text-gray-900">Price Calculator</span>
                    <span className="text-xs text-gray-500 ml-auto">Try different prices</span>
                  </div>

                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-1">
                      <label className="block text-sm text-gray-600 mb-1">Menu Price</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.50"
                          value={customPrice ? (customPrice / 100).toFixed(2) : ''}
                          onChange={(e) => setCustomPrice(Math.round(parseFloat(e.target.value || '0') * 100))}
                          className="w-full pl-7 pr-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg font-semibold"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="text-center px-4">
                      <div className="text-2xl font-bold text-gray-400">=</div>
                    </div>
                    <div className="flex-1">
                      {(() => {
                        const metrics = customPrice
                          ? calculatePriceMetrics(selectedRecipe.costPerPortionCents!, customPrice)
                          : null;

                        if (!metrics || !customPrice) {
                          return (
                            <div className="text-center py-3 text-gray-400">
                              Enter a price to see metrics
                            </div>
                          );
                        }

                        const isGood = metrics.foodCostPercent <= 35;
                        const isWarning = metrics.foodCostPercent > 35 && metrics.foodCostPercent <= 45;
                        const isBad = metrics.foodCostPercent > 45;

                        return (
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                              <div className="text-xs text-gray-500">Food Cost</div>
                              <div className={clsx(
                                'text-xl font-bold',
                                isGood && 'text-green-600',
                                isWarning && 'text-amber-600',
                                isBad && 'text-red-600'
                              )}>
                                {metrics.foodCostPercent.toFixed(1)}%
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500">Margin</div>
                              <div className={clsx(
                                'text-xl font-bold',
                                isGood && 'text-green-600',
                                isWarning && 'text-amber-600',
                                isBad && 'text-red-600'
                              )}>
                                {metrics.marginPercent.toFixed(1)}%
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500">Profit</div>
                              <div className={clsx(
                                'text-xl font-bold',
                                metrics.profitCents > 0 ? 'text-green-600' : 'text-red-600'
                              )}>
                                {formatCurrency(metrics.profitCents)}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Quick price buttons */}
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs text-gray-500 self-center mr-2">Quick set:</span>
                    {[15, 18, 22, 26, 32, 38, 45, 55].map(price => (
                      <button
                        key={price}
                        onClick={() => setCustomPrice(price * 100)}
                        className={clsx(
                          'px-3 py-1 text-sm rounded-full border transition-all',
                          customPrice === price * 100
                            ? 'bg-green-600 text-white border-green-600'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-green-400 hover:text-green-600'
                        )}
                      >
                        ${price}
                      </button>
                    ))}
                  </div>

                  {/* Status message */}
                  {customPrice && selectedRecipe.costPerPortionCents && (
                    <div className={clsx(
                      'mt-4 p-3 rounded-lg text-sm',
                      calculatePriceMetrics(selectedRecipe.costPerPortionCents, customPrice)!.foodCostPercent <= 30
                        ? 'bg-green-100 text-green-800'
                        : calculatePriceMetrics(selectedRecipe.costPerPortionCents, customPrice)!.foodCostPercent <= 35
                        ? 'bg-green-50 text-green-700'
                        : calculatePriceMetrics(selectedRecipe.costPerPortionCents, customPrice)!.foodCostPercent <= 45
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-red-100 text-red-800'
                    )}>
                      {(() => {
                        const fc = calculatePriceMetrics(selectedRecipe.costPerPortionCents!, customPrice)!.foodCostPercent;
                        if (fc <= 25) return '🌟 Excellent! Premium pricing with outstanding margins.';
                        if (fc <= 30) return '✅ Great! This is ideal for most restaurants.';
                        if (fc <= 35) return '👍 Good. Solid margins for casual dining.';
                        if (fc <= 45) return '⚠️ Caution. Consider raising the price for better margins.';
                        return '❌ Warning! This price will lose money. Increase significantly.';
                      })()}
                    </div>
                  )}

                  {/* Apply Price Button */}
                  {customPrice && customPrice > 0 && (
                    <div className="mt-4 flex justify-end">
                      {selectedRecipe.menuItemId ? (
                        <button
                          onClick={() => applyPrice(customPrice)}
                          disabled={updateMenuPriceMutation.isPending}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                        >
                          <CheckCircle2 size={16} />
                          {updateMenuPriceMutation.isPending ? 'Applying...' : `Apply ${formatCurrency(customPrice)} to Menu`}
                        </button>
                      ) : (
                        <button
                          onClick={() => createMenuItemFromPricing(customPrice)}
                          disabled={createMenuItemMutation.isPending || !newMenuItemName.trim()}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                        >
                          <Plus size={16} />
                          {createMenuItemMutation.isPending ? 'Creating...' : `Create Menu Item at ${formatCurrency(customPrice)}`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Industry Context */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="text-amber-500" size={18} />
                  <span className="font-medium text-gray-900">Industry Standards</span>
                </div>
                <div className="text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="mb-2">
                    <strong>Food cost percentage</strong> is the ratio of ingredient cost to menu price.
                    Most successful restaurants target <strong>28-35%</strong> food cost.
                  </p>
                  <p>
                    Higher-end establishments often achieve <strong>25-28%</strong>, while casual/fast-casual
                    may operate at <strong>32-40%</strong> to offer competitive prices.
                  </p>
                </div>
              </div>

              {/* Pricing Tiers */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="text-green-600" size={18} />
                  <span className="font-medium text-gray-900">Suggested Menu Prices</span>
                </div>

                {selectedRecipe.costPerPortionCents ? (
                  <div className="space-y-3">
                    {getPricingSuggestions(selectedRecipe.costPerPortionCents).map((tier, index) => {
                      const menuItem = selectedRecipe.menuItemId
                        ? menuItems.find(m => m.id === selectedRecipe.menuItemId)
                        : null;
                      const isCurrentPrice = menuItem &&
                        Math.abs(menuItem.priceCents - tier.suggestedPrice) < 100; // within $1

                      return (
                        <div
                          key={tier.name}
                          className={clsx(
                            'border rounded-lg p-4 transition-all',
                            isCurrentPrice
                              ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                              : 'border-gray-200 hover:border-amber-300'
                          )}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className={clsx(
                                  'font-semibold',
                                  index === 0 ? 'text-purple-700' :
                                  index === 1 ? 'text-green-700' :
                                  index === 2 ? 'text-amber-700' :
                                  'text-gray-700'
                                )}>
                                  {tier.name} Pricing
                                </span>
                                {isCurrentPrice && (
                                  <span className="flex items-center gap-1 text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">
                                    <CheckCircle2 size={12} />
                                    Current
                                  </span>
                                )}
                                {index === 1 && !isCurrentPrice && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                    Recommended
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mt-1">{tier.description}</p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-gray-900">
                                {formatCurrency(tier.suggestedPrice)}
                              </div>
                              <div className="text-xs text-gray-500">menu price</div>
                            </div>
                          </div>

                          <div className="mt-3 flex items-end justify-between pt-3 border-t">
                            <div className="grid grid-cols-3 gap-4 flex-1">
                              <div>
                                <div className="text-xs text-gray-500">Food Cost %</div>
                                <div className={clsx(
                                  'font-semibold',
                                  tier.foodCostPercent <= 30 ? 'text-green-600' :
                                  tier.foodCostPercent <= 35 ? 'text-amber-600' :
                                  'text-red-600'
                                )}>
                                  {tier.foodCostPercent}%
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500">Profit Margin</div>
                                <div className="font-semibold text-green-600">
                                  {tier.marginPercent}%
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500">Profit/Item</div>
                                <div className="font-semibold text-gray-900">
                                  {formatCurrency(tier.profitPerItem)}
                                </div>
                              </div>
                            </div>
                            {selectedRecipe.menuItemId && !isCurrentPrice && (
                              <button
                                onClick={() => applyPrice(tier.suggestedPrice)}
                                disabled={updateMenuPriceMutation.isPending}
                                className="ml-4 px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 whitespace-nowrap"
                              >
                                Apply Price
                              </button>
                            )}
                            {!selectedRecipe.menuItemId && (
                              <button
                                onClick={() => {
                                  setCustomPrice(tier.suggestedPrice);
                                  createMenuItemFromPricing(tier.suggestedPrice);
                                }}
                                disabled={createMenuItemMutation.isPending || !newMenuItemName.trim()}
                                className="ml-4 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
                              >
                                Create at This Price
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <AlertCircle className="mx-auto h-8 w-8 mb-2" />
                    <p>Add ingredients to calculate pricing suggestions</p>
                  </div>
                )}
              </div>

              {/* Break-even Analysis */}
              {selectedRecipe.costPerPortionCents && selectedRecipe.menuItemId && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <PieChart className="text-blue-600" size={18} />
                    <span className="font-medium text-gray-900">Break-Even Analysis</span>
                  </div>
                  {(() => {
                    const menuItem = menuItems.find(m => m.id === selectedRecipe.menuItemId);
                    if (!menuItem) return null;

                    const profitPerItem = menuItem.priceCents - (selectedRecipe.costPerPortionCents || 0);
                    const fixedCostsExample = 500000; // $5000 example fixed costs
                    const breakEvenUnits = profitPerItem > 0 ? Math.ceil(fixedCostsExample / profitPerItem) : 0;

                    return (
                      <div className="text-sm text-gray-600">
                        <p>
                          At <strong>{formatCurrency(menuItem.priceCents)}</strong> menu price,
                          you earn <strong>{formatCurrency(profitPerItem)}</strong> profit per item.
                        </p>
                        <p className="mt-2">
                          <span className="text-gray-500">Example:</span> To cover $5,000 in fixed costs,
                          you'd need to sell approximately <strong>{breakEvenUnits.toLocaleString('en-US')}</strong> portions.
                        </p>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="flex justify-between items-center p-4 border-t bg-gray-50">
              <p className="text-xs text-gray-500">
                Prices exclude taxes, overhead, and labor costs
              </p>
              <button
                onClick={() => setShowPricingModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
